import { Col } from "antd";
import AppAnimate from "@lib/components/AppComponents/AppAnimate";
import AppRowContainer from "@lib/components/AppComponents/AppRowContainer";
import AppsContainer from "@lib/components/AppComponents/AppsContainer";
import {
  DateRangeFilter,
  SelectedCustomerSelectBox,
} from "@lib/components/MiniComponents/Others";
import MiniInfoCard from "@lib/components/MiniComponents/Dashboard/MiniInfoCard";
import { FaFilter } from "react-icons/fa";
import { blue, magenta } from "@ant-design/colors";
import { FormattedMessage, useIntl } from "react-intl";
import {
  WalletAddressMiniCard,
  AIWhiteLabelMiniCard,
} from "@app/admin/dashboards/components/MiniCards";
import { useTypedSelector } from "@lib/redux";
import { useDateRange } from "@lib/hooks/useDateRange";
import { useMemo } from "react";
import { ApiParams, CurrencyEnum } from "@lib/types";
import dayjs, { type Dayjs } from "dayjs";
import { useQuery } from "@tanstack/react-query";
import { apiGetAILogs } from "@lib/api/aiLog";
import {
  AICustomerDailyUsageChart,
  AITaskDailyUsageChart,
  AIModelCostChart,
  AIModelCustomerUsageChart,
} from "@lib/components/MiniComponents/Dashboard/AI";
import { TbZoomMoney } from "react-icons/tb";
import { currencySymbol, formatCurrency } from "@lib/helpers/StringHelper";

export default function AIPaymentsDashboard() {
  const { messages } = useIntl();
  const { selectedCustomer } = useTypedSelector((state) => state.cache);

  const { dateBetween, setDateBetween } = useDateRange({
    initialRange: "monthly",
    cache: true,
  });

  const handleDateBetween = (date: [Dayjs, Dayjs]) => {
    setDateBetween(date);
  };

  const params = useMemo(() => {
    return {
      s: [
        {
          $and: [
            { createdAt: { $gte: dateBetween[0] } },
            { createdAt: { $lte: dateBetween[1] } },
            ...(selectedCustomer?.id
              ? [{ customerId: { $eq: selectedCustomer.id } }]
              : []),
          ],
        },
      ],
      join: ["customer"],
      sort: "createdAt,DESC",
      limit: 10000,
    } as ApiParams;
  }, [dateBetween, selectedCustomer]);

  const query = useQuery({
    queryKey: ["ai-payments-dashboard", JSON.stringify(params)],
    queryFn: () => apiGetAILogs(params),
    enabled: !!params,
  });

  const apiData = query?.data?.data?.data;

  const total = apiData?.reduce((acc, item) => acc + item.cost, 0) || 0;
  const todayTotal =
    apiData
      ?.filter((item) => dayjs(item.createdAt).isSame(dayjs(), "day"))
      .reduce((acc, item) => acc + item.cost, 0) || 0;

  return (
    <AppsContainer
      title={messages["sidebar.dashboard.aiPayments"] as string}
      fullView
      cardStyle={{
        background: "transparent",
        boxShadow: "none",
      }}
      showDateRange={true}
      dateRangeValue={dateBetween}
      onDateRangeChange={handleDateBetween}
    >
      <AppAnimate animation="transition.slideUpIn" delay={200}>
        <AppRowContainer>
          <Col xs={24} md={12} lg={6} xl={6}>
            <AIWhiteLabelMiniCard cardProps={{ borderFull: true }} />
          </Col>
          <Col xs={24} md={12} lg={6} xl={6}>
            <WalletAddressMiniCard cardProps={{ borderFull: true }} type="ai" />
          </Col>
          <Col xs={24} md={12} lg={6} xl={6}>
            <MiniInfoCard
              loading={query?.isLoading || query?.isFetching}
              data={{
                icon: <TbZoomMoney />,
                color: magenta[6],
                desc: (
                  <div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "flex-start",
                      }}
                    >
                      <FormattedMessage id="common.aiPayments" />
                      <DateRangeFilter
                        value={dateBetween}
                        onlyShowDate
                        showDateStyle={{ fontSize: 10 }}
                      />
                    </div>
                  </div>
                ),
                title: `${formatCurrency(total)} ${currencySymbol(CurrencyEnum.USD)}`,
                rightMenu: {
                  key: "new",
                  data: {
                    title: "",
                    data: `${formatCurrency(todayTotal)} ${currencySymbol(CurrencyEnum.USD)}`,
                    duration: messages["common.today"] as string,
                  },
                },
              }}
            />
          </Col>
          <Col xs={24} md={12} lg={6} xl={6}>
            <MiniInfoCard
              data={{
                icon: <FaFilter />,
                color: blue[5],
                desc: <SelectedCustomerSelectBox />,
              }}
              cardProps={{ borderFull: true }}
            />
          </Col>

          <Col xs={24} md={24} lg={12} xl={12}>
            <AIModelCostChart
              query={query}
              extra={<DateRangeFilter value={dateBetween} onlyShowDate />}
              refetch={() => query.refetch()}
            />
          </Col>
          <Col xs={24} md={24} lg={12} xl={12}>
            <AIModelCustomerUsageChart
              query={query}
              extra={<DateRangeFilter value={dateBetween} onlyShowDate />}
              refetch={() => query.refetch()}
            />
          </Col>

          <Col xs={24} md={24} lg={24} xl={24}>
            <AITaskDailyUsageChart
              query={query}
              extra={<DateRangeFilter value={dateBetween} onlyShowDate />}
              refetch={() => query.refetch()}
            />
          </Col>

          <Col xs={24} md={24} lg={24} xl={24}>
            <AICustomerDailyUsageChart
              query={query}
              extra={<DateRangeFilter value={dateBetween} onlyShowDate />}
              refetch={() => query.refetch()}
            />
          </Col>
        </AppRowContainer>
      </AppAnimate>
    </AppsContainer>
  );
}
